import {customerContext, getSalesAreaFields} from '@theme/rs-current-company-location';
import { getCustomerAppBaseUrl } from "@theme/gadget-envs";

class SubmittedReturnsTable {
  constructor() {
    this.tableBody = document.querySelector('.submitted-returns-table__body');
    this.searchInput = document.getElementById('submitted-returns-search-input');
    this.searchButton = document.querySelector('.submitted-returns-search__button');
    this.dateRangeInput = document.getElementById('submitted-returns-date-range-input');
    this.dateRangeWrapper = document.querySelector('.submitted-returns-date-range');
    this.table = document.querySelector('.submitted-returns-table');
    this.paginationWrapper = document.querySelector('.submitted-returns-pagination-wrapper');
    /** @type {any[]} */
    this.currentReturns = [];
    /** @type {boolean|null} Whether company has any returns (from page load check) */
    this.companyHasReturns = null;
    /** @type {AbortController|null} Abort in-flight request when a new one is triggered */
    this.fetchAbortController = null;

    this.init();
  }

  init() {
    if (!this.tableBody) return;

    // Debounce filter-triggered API calls so we only submit once user is done (e.g. selecting both dateFrom and dateTo)
    this.debouncedFetch = this.debounce((dateRange, searchTerm) => {
      this.getSubmittedReturns(dateRange, searchTerm);
    }, 500);

    this.initDateRange();
    this.initSearch();
    this.initSearchClear();
    this.initWarrantyReturnBanner();

    // Load with default date range (90 days per submitted-returns-date-range default)
    const dateRange = this.dateRangeInput?.value || '90';
    const searchTerm = this.searchInput?.value || '';
    this.getSubmittedReturns(dateRange, searchTerm);
  }

  /**
   * Debounce a function - only invoke after `wait` ms of no further calls.
   * @param {Function} fn
   * @param {number} wait - ms to wait
   * @returns {Function}
   */
  debounce(fn, wait) {
    let timeout = null;
    return (...args) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        timeout = null;
        fn.apply(this, args);
      }, wait);
    };
  }

  /**
   * If warranty_return_submitted cookie exists: show banner, clear cookie, auto-close after 5s.
   */
  initWarrantyReturnBanner() {
    const cookieName = 'warranty_return_submitted';
    const hasCookie = document.cookie.split(';').some((s) => s.trim().startsWith(cookieName + '='));
    if (!hasCookie) return;

    const banner = document.querySelector('.rs-banner--warranty-returns');
    if (!banner) return;

    banner.classList.add('rs-banner--visible');

    // Clear cookie (must use same path as when set)
    document.cookie = `${cookieName}=; path=/; max-age=0`;

    // Auto-close after 5 seconds
    setTimeout(() => {
      const closeBtn = banner.querySelector('.rs-banner__close-button');
      if (closeBtn) {
        closeBtn.click();
      } else {
        banner.classList.remove('rs-banner--visible');
      }
    }, 5000);
  }

  /**
   * Check if company has any returns (broad date range) - runs in background
   */
  async checkCompanyHasReturns() {
    const dateTo = new Date().toISOString().split('T')[0];
    try {
      const checkData = await this.getSubmittedReturnsAPI('1975-09-04', dateTo, '');
      const checkReturns = checkData?.warrantyReturnHistoryResponse?.transactions ?? [];
      this.companyHasReturns = Array.isArray(checkReturns) && checkReturns.length > 0;
    } catch {
      this.companyHasReturns = null;
    }
  }

  initDateRange() {
    if (!this.dateRangeInput) return;

    const triggerFetch = () => {
      const dateRange = this.dateRangeInput?.value || '90';
      const searchTerm = this.searchInput?.value || '';
      this.debouncedFetch(dateRange, searchTerm);
    };

    this.dateRangeInput.addEventListener('change', triggerFetch);
  }

  initSearch() {
    if (!this.searchButton) return;

    this.searchButton.addEventListener('click', () => {
      const dateRange = this.dateRangeInput?.value || '90';
      const searchTerm = this.searchInput?.value || '';
      this.getSubmittedReturns(dateRange, searchTerm);
    });

    if (this.searchInput) {
      this.searchInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const dateRange = this.dateRangeInput?.value || '90';
          const searchTerm = this.searchInput?.value || '';
          this.getSubmittedReturns(dateRange, searchTerm);
        }
      });
    }
  }

  /** Clear (X) control inside the submitted returns search field. */
  initSearchClear() {
    const searchInput = this.searchInput;
    if (!searchInput) return;

    if (searchInput.dataset.rsSubmittedReturnsSearchInputWired !== '1') {
      searchInput.dataset.rsSubmittedReturnsSearchInputWired = '1';
      searchInput.addEventListener('input', () => {
        this.syncSearchClearUi();
      });
    }

    const inputWrapper = searchInput.closest('.rs-input__wrapper');
    if (!inputWrapper) return;

    if (inputWrapper.dataset.rsSubmittedReturnsSearchClearWired === '1') {
      this.searchClearBtn = inputWrapper.querySelector('.submitted-returns-search-clear');
      this.syncSearchClearUi();
      return;
    }
    inputWrapper.dataset.rsSubmittedReturnsSearchClearWired = '1';

    const clearLabel =
      typeof window.__t === 'function' ? window.__t('rs_profile_locations.clear_search') : 'Clear search';
    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'rs-button-unstyled submitted-returns-search-clear rs-hidden';
    clearBtn.setAttribute('aria-label', clearLabel);
    clearBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" class="rs-icon rs-icon-close" aria-hidden="true" focusable="false">' +
      '<path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';
    clearBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      searchInput.value = '';
      this.syncSearchClearUi();
      searchInput.focus();
      const dateRange = this.dateRangeInput?.value || '90';
      this.getSubmittedReturns(dateRange, '');
    });
    inputWrapper.appendChild(clearBtn);
    this.searchClearBtn = clearBtn;
    this.syncSearchClearUi();
  }

  syncSearchClearUi() {
    const searchInput = this.searchInput;
    const clearBtn = this.searchClearBtn;
    if (!searchInput || !clearBtn) return;
    const hasVal = (searchInput.value || '').trim().length > 0;
    clearBtn.classList.toggle('rs-hidden', !hasVal);
    searchInput.classList.toggle('submitted-returns-search__input--has-value', hasVal);
  }

  /**
   * Parse date range value to dateFrom/dateTo
   * @param {string} dateRange - 'custom:YYYY-MM-DD:YYYY-MM-DD' or preset days
   * @returns {{dateFrom: string, dateTo: string}}
   */
  calculateDateRange(dateRange) {
    if (dateRange && dateRange.includes('custom')) {
      const [startDate, endDate] = dateRange.replace('custom:', '').split(':');
      return { dateFrom: startDate, dateTo: endDate };
    }
    const dateTo = new Date().toISOString().split('T')[0];
    const dateFromDate = new Date();
    dateFromDate.setDate(dateFromDate.getDate() - parseInt(dateRange || '90', 10));
    const dateFrom = dateFromDate.toISOString().split('T')[0];
    return { dateFrom, dateTo };
  }

  /**
   * Call warranty-returns submitted search API
   * @param {string} dateFrom - YYYY-MM-DD
   * @param {string} dateTo - YYYY-MM-DD
   * @param {string} searchTerm - Search term
   * @param {AbortSignal} [signal] - AbortSignal to cancel request
   * @returns {Promise<any>}
   */
  async getSubmittedReturnsAPI(dateFrom, dateTo, searchTerm, signal) {
    const salesAreaFields = getSalesAreaFields(customerContext);
    const salesOrg = salesAreaFields.sales_org_id || '';
    const distributionChannel = salesAreaFields.distribution_channel_id || '';
    const division = salesAreaFields.division_id || '';
    const soldTo = customerContext?.location_external_id ? document.body.dataset.soldTo : '';
    const shipTo = customerContext?.ship_to_id ? document.body.dataset.shipTo : '';

    const response = await fetch(
      `${getCustomerAppBaseUrl()}/api/warranty-returns/submitted/search`,
      {
        method: 'POST',
        ...(signal && { signal }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${window.myJWT}`,
        },
        body: JSON.stringify({
          salesOrg,
          distributionChannel,
          division,
          soldTo,
          shipTo: [shipTo],
          filters: {
            searchTerm: searchTerm || '',
            dateFrom,
            dateTo,
          },
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Format submitted date for display. `new Date('YYYY-MM-DD')` is UTC midnight, so
   * toLocaleDateString() can show the previous calendar day in US timezones. We parse
   * the YYYY-MM-DD prefix as a local calendar date instead.
   * @param {string} raw
   * @returns {string}
   */
  formatSubmittedDateForDisplay(raw) {
    if (raw == null || raw === '') return '';
    const s = String(raw).trim();
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (m) {
      const y = Number(m[1]);
      const mo = Number(m[2]) - 1;
      const d = Number(m[3]);
      if (!Number.isNaN(y) && !Number.isNaN(mo) && !Number.isNaN(d)) {
        return new Date(y, mo, d).toLocaleDateString();
      }
    }
    try {
      return new Date(s).toLocaleDateString();
    } catch {
      return s;
    }
  }

  /**
   * Get document URL from transactions API
   * @param {string} documentNumber - Document/credit memo number
   * @returns {Promise<string|null>} Document URL or null if error
   */
  async getDocumentUrl(documentNumber) {
    if (!documentNumber) return null;

    try {
      const soldTo = customerContext?.company_external_id || '';
      const response = await fetch(
        `${getCustomerAppBaseUrl()}/api/transactions/document?documentNumber=${documentNumber}&documentType=INVOICE&soldTo=${soldTo}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${window.myJWT}`,
          },
        }
      );

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/pdf')) {
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      }

      try {
        const data = await response.json();
        if (data.url) return data.url;
        if (data.pdfUrl) return data.pdfUrl;
      } catch {
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      }

      return null;
    } catch (error) {
      console.error('Error fetching document:', error);
      return null;
    }
  }

  bindDocumentLinks() {
    const docLinks = document.querySelectorAll('.submitted-returns-table__view-details-link');
    docLinks.forEach((link) => {
      link.addEventListener('click', async (e) => {
        e.preventDefault();
        const documentNumber = link.dataset.documentNumber;
        const docUrl = await this.getDocumentUrl(documentNumber);
        if (docUrl) {
          link.href = docUrl;
          window.open(docUrl, '_blank');
        } else {
          this.showToast();
        }
      });
    });
  }

  showToast() {
    document.querySelectorAll('.submitted-returns-toast').forEach((el) => el.remove());

    document.body.insertAdjacentHTML('beforeend', `<div class="submitted-returns-toast" role="alert" aria-live="assertive" aria-atomic="true">
      <div class="submitted-returns-toast__content">
        <svg class="submitted-returns-toast__icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M18.109 14.9999L11.4423 3.33319C11.297 3.0767 11.0862 2.86335 10.8314 2.71492C10.5767 2.56649 10.2872 2.48828 9.99234 2.48828C9.69752 2.48828 9.40797 2.56649 9.15324 2.71492C8.8985 2.86335 8.6877 3.0767 8.54234 3.33319L1.87567 14.9999C1.72874 15.2543 1.6517 15.5431 1.65235 15.837C1.653 16.1308 1.73132 16.4192 1.87938 16.673C2.02744 16.9269 2.23996 17.137 2.49542 17.2822C2.75088 17.4274 3.04018 17.5025 3.33401 17.4999H16.6673C16.9598 17.4996 17.2469 17.4223 17.5001 17.2759C17.7532 17.1295 17.9634 16.9191 18.1094 16.6658C18.2555 16.4125 18.3324 16.1252 18.3323 15.8328C18.3322 15.5404 18.2552 15.2531 18.109 14.9999Z" stroke="currentColor" stroke-width="1.45833" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10 7.5V10.8333" stroke="currentColor" stroke-width="1.45833" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10 14.1667H10.0083" stroke="currentColor" stroke-width="1.45833" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <p class="submitted-returns-toast__message">PDF unavailable. Please try again later.</p>
        <button class="submitted-returns-toast__close rs-button-unstyled" type="button" aria-label="Close notification">
          <svg class="rs-icon rs-icon-close" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M10.7197 0.219727C11.0126 -0.0731381 11.4874 -0.0731574 11.7803 0.219727C12.0729 0.512631 12.0731 0.987463 11.7803 1.28027L7.06055 6L11.7803 10.7197L11.8311 10.7764C12.0713 11.0709 12.0548 11.5057 11.7803 11.7803C11.5057 12.0548 11.0709 12.0722 10.7764 11.832L10.7197 11.7803L6 7.06055L1.28027 11.7803C0.987466 12.073 0.512632 12.0729 0.219727 11.7803C-0.0731579 11.4874 -0.0731387 11.0126 0.219727 10.7197L4.93945 6L0.219727 1.28027C-0.0731675 0.98738 -0.0731675 0.51262 0.219727 0.219727C0.512628 -0.0730756 0.98741 -0.0731365 1.28027 0.219727L6 4.93945L10.7197 0.219727Z" fill="#323232"/>
          </svg>
        </button>
      </div>
    </div>`);

    const toast = document.querySelector('.submitted-returns-toast');
    const closeButton = toast?.querySelector('.submitted-returns-toast__close');
    closeButton?.addEventListener('click', () => toast?.remove());
    setTimeout(() => toast?.remove(), 3000);
  }

  /**
   * Get empty state message based on whether company has returns and current filters
   * @param {string} searchTerm - Current search term
   * @returns {string}
   */
  getEmptyStateMessage(searchTerm) {
    if (searchTerm) {
      return 'No results to display. Use the search field or date range to view available records.';
    }
    if (this.companyHasReturns === true) {
      return 'No results to display. Use the search field or date range to view available records.';
    }
    return "You don't have any submitted returns yet. New returns will appear here once submitted.";
  }

  /**
   * Update empty state with icon and message
   * @param {'search'|'box'} icon - Icon type
   * @param {string} message - Message to display
   */
  updateEmptyState(icon, message) {
    if (!this.tableBody || !this.table) return;

    const iconUrl = this.table.getAttribute(`data-icon-${icon}`);
    const displayMessage = message || 'No results to display. Use the search field or date range to view available records.';

    this.tableBody.innerHTML = `
      <tr class="submitted-returns-table__empty">
        <td class="text-center" colspan="7">
          <div class="submitted-returns-table__icon">
            <img src="${iconUrl}" alt="" width="24" height="24">
          </div>
          <p class="text-large weight-500 text-brand-black">${displayMessage}</p>
        </td>
      </tr>
    `;
  }

  /**
   * Initialize pagination - show/hide rows based on .rs-cart-pagination__rows-option.rs-active
   * @param {number} totalItems
   */
  initPagination(totalItems) {
    if (!this.paginationWrapper) return;

    const rows = this.tableBody?.querySelectorAll('.submitted-returns-table__row-return') || [];
    const optionButtons = this.paginationWrapper.querySelectorAll('.submitted-returns-pagination__rows-options .rs-cart-pagination__rows-option');
    const rowSelectBtn = this.paginationWrapper.querySelector('.submitted-returns-pagination__rows-button');
    const prevBtn = document.getElementById('submitted-returns-pagination-prev');
    const nextBtn = document.getElementById('submitted-returns-pagination-next');
    const pageInfo = this.paginationWrapper.querySelector('.submitted-returns-pagination__info');
    const rowsOptions = this.paginationWrapper.querySelector('.submitted-returns-pagination__rows-options');

    if (!rows.length || !optionButtons.length || !prevBtn || !nextBtn || !pageInfo) return;

    this._paginationCurrentPage = 1;

    const renderPage = () => {
      const totalItems = this.currentReturns.length;
      const rowsPerPage = parseInt(
        this.paginationWrapper?.querySelector('.rs-cart-pagination__rows-option.rs-active')?.getAttribute('value') || '10',
        10
      );
      const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));
      let currentPage = this._paginationCurrentPage;

      if (currentPage > totalPages) currentPage = totalPages;
      if (currentPage < 1) currentPage = 1;
      this._paginationCurrentPage = currentPage;

      const startIndex = (currentPage - 1) * rowsPerPage + 1;
      const endIndex = Math.min(startIndex + rowsPerPage - 1, totalItems);

      const currentRows = this.tableBody?.querySelectorAll('.submitted-returns-table__row-return') || [];
      currentRows.forEach((row) => {
        const idx = parseInt(row.getAttribute('data-index') || '0', 10);
        if (idx >= startIndex && idx <= endIndex) {
          row.classList.remove('rs-hidden');
        } else {
          row.classList.add('rs-hidden');
        }
      });

      pageInfo.textContent = `${startIndex}-${endIndex} of ${totalItems}`;
      prevBtn.disabled = currentPage === 1;
      nextBtn.disabled = currentPage === totalPages;
    };

    if (!this.paginationWrapper.dataset.listenersAdded) {
      this.paginationWrapper.dataset.listenersAdded = 'true';

      nextBtn.addEventListener('click', () => {
        const totalPages = Math.max(1, Math.ceil(this.currentReturns.length / parseInt(this.paginationWrapper?.querySelector('.rs-cart-pagination__rows-option.rs-active')?.getAttribute('value') || '10', 10)));
        if (this._paginationCurrentPage < totalPages) {
          this._paginationCurrentPage++;
          renderPage();
        }
      });

      prevBtn.addEventListener('click', () => {
        if (this._paginationCurrentPage > 1) {
          this._paginationCurrentPage--;
          renderPage();
        }
      });

      optionButtons.forEach((option) => {
        option.addEventListener('click', () => {
          optionButtons.forEach((btn) => btn.classList.remove('rs-active'));
          option.classList.add('rs-active');
          this._paginationCurrentPage = 1;
          const span = rowSelectBtn?.querySelector('span');
          if (span) span.textContent = window.__t('show_count_rows', { count: option.getAttribute('value') });
          renderPage();
        });
      });

      rowSelectBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        const isExpanded = rowSelectBtn.getAttribute('aria-expanded') === 'true';
        rowSelectBtn.setAttribute('aria-expanded', String(!isExpanded));
        if (rowsOptions) rowsOptions.style.display = isExpanded ? 'none' : 'block';
      });

      document.addEventListener('click', (e) => {
        if (rowSelectBtn && rowsOptions && !rowSelectBtn.contains(e.target) && !rowsOptions.contains(e.target)) {
          rowSelectBtn.setAttribute('aria-expanded', 'false');
          rowsOptions.style.display = 'none';
        }
      });
    }

    this._paginationCurrentPage = 1;
    renderPage();
  }

  /**
   * Update results count in header
   * @param {number} count
   */
  updateResultsCount(count) {
    const countEl = document.querySelector('.submitted-returns--header__title-count');
    if (countEl) {
      countEl.textContent = window.__t('count_results', { count: count });
    }
  }

  /**
   * Fetch and display submitted returns
   * @param {string} dateRange - Date range value
   * @param {string} searchTerm - Search term
   */
  async getSubmittedReturns(dateRange, searchTerm) {
    if (!dateRange) return;

    this.fetchAbortController?.abort();
    this.fetchAbortController = new AbortController();
    const signal = this.fetchAbortController.signal;

    const { dateFrom, dateTo } = this.calculateDateRange(dateRange);

    this.showLoadingState();

    if (this.paginationWrapper) {
      this.paginationWrapper.style.display = 'none';
    }

    try {
      const data = await this.getSubmittedReturnsAPI(dateFrom, dateTo, searchTerm, signal);

      // Expect API to return array of returns - adjust path based on actual API response structure
      let returns = data?.warrantyReturnHistoryResponse?.transactions ?? [];

      // Client-side filter by Web ID Number and customer reference/PO #
      if (searchTerm && searchTerm.trim()) {
        const term = searchTerm.trim().toLowerCase();
        returns = returns.filter((r) => {
          const webId = String(r.webOrderNumber ?? '').toLowerCase();
          const customerRef = String(r.customerReference ?? r.customer_reference ?? r.poNumber ?? r.po_number ?? '').toLowerCase();
          return webId.includes(term) || customerRef.includes(term);
        });
      }

      if (returns && returns.length > 0) {
        this.companyHasReturns = true;
        this.currentReturns = returns;
        this.updateTable(returns);
        this.bindDocumentLinks();
        this.updateResultsCount(returns.length);
        this.initPagination(returns.length);

        if (this.paginationWrapper) {
          this.paginationWrapper.style.display = '';
        }
      } else {
        this.currentReturns = [];
        this.updateResultsCount(0);
        const icon = searchTerm ? 'search' : 'box';
        const message = this.getEmptyStateMessage(searchTerm);
        this.updateEmptyState(icon, message);

        if (this.paginationWrapper) {
          this.paginationWrapper.style.display = 'none';
        }
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;

      console.error('Error fetching submitted returns:', error);

      // Retry with broad date range to see if user needs to adjust filters vs actual API error
      const fallbackDateTo = new Date().toISOString().split('T')[0];
      try {
        const fallbackData = await this.getSubmittedReturnsAPI('1975-09-04', fallbackDateTo, searchTerm);
        const fallbackReturns = fallbackData?.warrantyReturnHistoryResponse?.transactions ?? [];

        if (fallbackReturns && fallbackReturns.length > 0) {
          this.companyHasReturns = true;
        } else {
          this.companyHasReturns = false;
        }
        this.currentReturns = [];
        this.updateResultsCount(0);
        this.updateEmptyState('search', this.getEmptyStateMessage(searchTerm));
        if (this.paginationWrapper) {
          this.paginationWrapper.style.display = 'none';
        }
      } catch (retryError) {
        console.error('Retry fetch also failed:', retryError);
        this.currentReturns = [];
        this.updateResultsCount(0);
        this.updateEmptyState('box', this.getEmptyStateMessage(searchTerm));
        if (this.paginationWrapper) {
          this.paginationWrapper.style.display = 'none';
        }
      }
    }
  }

  showLoadingState() {
    if (!this.tableBody) return;

    this.tableBody.innerHTML = `
      <tr class="submitted-returns-table__loading">
        <td class="text-center" colspan="7">
          <p class="text-large weight-500 text-brand-black">Loading...</p>
        </td>
      </tr>
    `;
  }

  /**
   * Render table rows from API data
   * @param {any[]} returns - Array of return objects
   */
  updateTable(returns) {
    if (!this.tableBody) return;

    this.tableBody.innerHTML = '';

    const rows = returns.map((returnItem, index) => {
      const webId = returnItem.webId ?? returnItem.web_id ?? returnItem.webID ?? returnItem.webOrderNumber ?? '';
      const customerRef = returnItem.customerReference ?? returnItem.customer_reference ?? returnItem.poNumber ?? returnItem.po_number ?? '';
      const status = returnItem.status ?? '';
      const dateSubmitted = returnItem.dateSubmitted ?? returnItem.date_submitted ?? returnItem.submittedDate ?? '';
      const submittedBy = returnItem.submittedBy ?? returnItem.submitted_by ?? '';
      const totalCredit = returnItem.creditMemoValue ?? '';
      const documentNumber = returnItem.creditMemoNumber ?? returnItem.credit_memo_number ?? '';

      const formattedDate = dateSubmitted ? this.formatSubmittedDateForDisplay(dateSubmitted) : '';

      const docLink = documentNumber
        ? `<a href="" class="submitted-returns-table__view-details-link link-blue weight-500" data-document-number="${documentNumber}" target="_blank" rel="noopener noreferrer">
          Credit Memo 
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 11.3333V2" stroke="#1174C3" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M4 7.33301L8 11.333L12 7.33301" stroke="#1174C3" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12.6654 14H3.33203" stroke="#1174C3" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>`
        : '';

      const statusClass = status ? `status--${status.toLowerCase().replace(/\s/g, '-')}` : '';

      return `
        <tr class="submitted-returns-table__row submitted-returns-table__row-return" data-index="${index + 1}">
          <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Web ID Number</p>${webId || ''}</td>
          <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">customer reference/PO #</p>${customerRef || ''}</td>
          <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Status</p><span class="submitted-returns-table__status ${statusClass} text-xsmall weight-500">${status || ''}</span></td>
          <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Date Submitted</p>${formattedDate || ''}</td>
          <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Submitted By</p>${submittedBy || ''}</td>
          <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Documentation</p>${docLink}</td>
        </tr>
      `;
    });

    this.tableBody.innerHTML = rows.join('');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (!document.querySelector('.submitted-returns-table')) return;

  const checkJWT = () => {
    if (window.myJWT) {
      new SubmittedReturnsTable();
    } else {
      setTimeout(checkJWT, 100);
    }
  };
  checkJWT();
});
